import { Modal } from 'antd';
import { useFormikContext } from 'formik';
import { isFinite, isNil, orderBy } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinanceProductFormValues } from '../..';
import { FinanceProductBasedOn } from '../../../../../../api/types';
import CollapsibleWrapper, { Panel } from '../../../../../../components/wrappers/CollapsibleWrapper';
import { useFinanceProductDetailContext } from '../../../FinanceProductDetailContext';
import ExportExcel from '../../../shared/ExportExcel';
import ImportExcel from '../../../shared/ImportExcel';
import useFinanceProductOptions from '../../../shared/useFinanceProductOptions';
import EditableTable, { type Columns } from '../EditableTable';
import { type TableRecord, type TableProps } from './types';

const useBases = (values: FinanceProductFormValues) => {
    const { basedOn, interestRate } = values;
    const { type } = interestRate || {};

    return useMemo<number[]>(() => {
        if (type !== 'table') {
            return [];
        }

        const { min, max, step } = interestRate;
        const isBaseValid = isFinite(min) && isFinite(max) && isFinite(step);

        if (isBaseValid && (basedOn === FinanceProductBasedOn.Loan || basedOn === FinanceProductBasedOn.DownPayment)) {
            const result = [];
            for (let base = min; base <= max; base += step) {
                result.push(base);
            }

            return result;
        }

        return [];
    }, [type, basedOn, interestRate]);
};

const useRecords = (values: FinanceProductFormValues, bases: number[]) => {
    const { term, interestRate } = values;
    const { min: termMin, max: termMax, step: termStep } = term || {};
    const { table, type } = interestRate || {};

    return useMemo<TableRecord[]>(() => {
        if (type !== 'table' || !isFinite(termMin) || !isFinite(termMax) || !isFinite(termStep)) {
            return [];
        }

        const result = [];
        for (let term = termMin; term <= termMax; term += termStep) {
            bases.forEach(base => {
                result.push({
                    term,
                    base,
                    value: table?.find(
                        ({ term: targetTerm, base: targetBase }) => term === targetTerm && base === targetBase
                    )?.value,
                    key: `${base} - ${term}`,
                });
            });
        }

        return result;
    }, [type, bases, termMin, termMax, termStep, table]);
};

const isMatch = (tableDataList, records) => {
    if (tableDataList.length !== records.length) {
        return false;
    }

    const find = tableDataList.some(
        (element, index) => !records.find(i => i.term === element.term && i.base === element.base)
    );

    return !find;
};

const StepTable = ({ disabled = false, onExport, mappedHeaders }: TableProps) => {
    const { t } = useTranslation(['financeProductDetails']);

    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();

    const { basedOn } = values;

    const { bank } = useFinanceProductDetailContext();
    const { amountUnitOptions } = useFinanceProductOptions(bank?.module?.companyId);

    const bases = useBases(values);
    const records = useRecords(values, bases);

    const onUpdate = useCallback(
        (record: TableRecord) => {
            const { term: targetTerm, base: targetBase } = record;
            const index = records.findIndex(({ term, base }) => term === targetTerm && base === targetBase);

            // clone current records
            const current = [...records];
            // update target
            current.splice(index, 1, record);

            setFieldValue(
                'interestRate.table',
                current.filter(({ value }) => isFinite(value)).map(({ term, base, value }) => ({ term, base, value }))
            );
        },
        [records, setFieldValue]
    );

    const columns = useMemo<Columns<TableRecord>>(
        () => [
            { title: t('financeProductDetails:tables.term.title'), dataIndex: 'term', fixed: true },
            {
                title: (
                    <>
                        {t(
                            `financeProductDetails:tables.interestRate.columnTitles.${
                                basedOn === FinanceProductBasedOn.DownPayment ? 'downPaymentTitle' : 'loanTitle'
                            }`
                        )}{' '}
                        {amountUnitOptions.find(item => item.value === values?.interestRate?.unit)?.label}
                    </>
                ),
                dataIndex: 'base',
            },
            {
                title: t('financeProductDetails:tables.interestRate.title'),
                dataIndex: 'value',
                onCell: record => ({ record, dataIndex: 'value', isEditable: !disabled, onUpdate }),
            },
        ],
        [onUpdate, t, basedOn, disabled, amountUnitOptions, values?.interestRate?.unit]
    );

    const onImport = useCallback(
        async data => {
            const tableDataList = data
                .filter(record => !isNil(record.value) && parseFloat(record.value) >= 0)
                .map(value => ({
                    term: value.term,
                    base: value.base,
                    value: parseFloat(value.value),
                }));

            if (isMatch(tableDataList, records)) {
                setFieldValue('interestRate.table', tableDataList);

                return;
            }

            Modal.confirm({
                title: t('financeProductDetails:importInterestRateStepTableModal.title'),
                content: t('financeProductDetails:importInterestRateStepTableModal.content'),
                okText: t('financeProductDetails:importInterestRateStepTableModal.okText'),
                okType: 'primary',
                cancelText: t('financeProductDetails:importInterestRateStepTableModal.cancelText'),
                onOk() {
                    setFieldValue('interestRate.isGenerateStepsInTable', false);
                    setFieldValue('interestRate.table', orderBy('term', 'asc', tableDataList));
                },
                onCancel() {
                    setFieldValue('interestRate.table', tableDataList);
                },
            });
        },
        [records, t, setFieldValue]
    );

    return (
        <CollapsibleWrapper defaultActiveKey={['interestRate']}>
            <Panel key="interestRate" header={t('financeProductDetails:tables.interestRate.title')}>
                <EditableTable columns={columns} records={records} />
            </Panel>
            <ImportExcel
                mappedHeaders={mappedHeaders}
                onImport={onImport}
                tableType="matrix"
                title={t('financeProductDetails:importModal.titles.interestRate')}
            />
            <ExportExcel onClick={() => onExport(records)} />
        </CollapsibleWrapper>
    );
};

export default StepTable;
